/**
 * The Listings API calls (server: /api/listings, docs/listings/README.md).
 * Plain functions over httpClient, one per endpoint; the React Query hooks in
 * ./hooks.ts are the only callers. Every answer is `{ success, data, … }`.
 */
import { HttpClientError, httpClient } from '@/api/http-client';
import type { ListingDeactivationReason } from '@aiga/app-config/listings';
import { env } from '@/lib/env';
import type {
  ActivityPage,
  AddTaskInput,
  AddUnitInput,
  AgreementBoard,
  AgreementFile,
  AgreementFileKind,
  ArchivedAgreementFile,
  AgreementWarning,
  AgreementFileUrl,
  AgreementFilePreviewUrls,
  AgreementInput,
  AgreementLink,
  AgreementList,
  AgreementStatus,
  BrokerRequest,
  CompleteTaskInput,
  ListingAgreement,
  ListingInput,
  ListingTask,
  MyTasks,
  PropertyLink,
  ResearchMatch,
  SearchUnit,
  TaskInput,
  TaskStep,
  TaskTemplate,
  TeamWorkload,
  TeamWorkloadByAssigner,
  TemplateInput,
  TemplateTrack,
} from './types';

const BASE = '/listings';

interface Envelope<T> {
  success: boolean;
  data: T;
}

/** `?a=1&b=2` from the defined, non-empty values ('' when none). */
export function queryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    q.set(key, String(value));
  }
  const text = q.toString();
  return text ? `?${text}` : '';
}

/** The answer's `data`. Every call names its httpClient method and whole path, so the URL contract test reads each one. */
const data = async <T>(res: Promise<Envelope<T>>): Promise<T> => (await res).data;

export interface AgreementListParams {
  status?: AgreementStatus | null;
  /** M74: the agreements this person holds an open task on. */
  assistant?: string | null;
  /** The projects this person assists (My listings card): M74, an open task assigned to them. */
  assisting?: string | null;
  broker?: string | null;
  /** M185: the agreements that list this property or a unit of it (the property page's Listing agreements card). */
  property?: string | null;
  q?: string | null;
  page?: number;
  limit?: number;
}

/** An agreement document's kind and / or (M198) its property; `property_id: null` = the whole agreement. */
const updateFile = (agreementId: string, fileId: string, body: { kind?: AgreementFileKind; property_id?: string | null }) =>
  data(httpClient.patch<Envelope<AgreementFile>>(`${BASE}/agreements/${agreementId}/files/${fileId}`, body));

export const listingsApi = {
  // ── Reads ──
  listAgreements: async (params: AgreementListParams): Promise<AgreementList> => {
    const res = await httpClient.get<Envelope<AgreementList['rows']> & { total: number; page: number; limit: number }>(
      `${BASE}/agreements${queryString({ ...params })}`,
    );
    return { rows: res.data, total: res.total, page: res.page, limit: res.limit };
  },
  getBoard: (agreementId: string) => data(httpClient.get<Envelope<AgreementBoard>>(`${BASE}/agreements/${agreementId}`)),
  /** `event_types`: comma-separated; only those types (the panel's Latest update). `task_id`: one task's rows (M206: with its step ticks for listings_work). */
  getActivity: async (
    agreementId: string,
    params: { listing_id?: string | null; task_id?: string | null; cursor?: string | null; limit?: number; event_types?: string },
  ): Promise<ActivityPage> => {
    const res = await httpClient.get<Envelope<ActivityPage['items']> & { next_cursor: string | null }>(
      `${BASE}/agreements/${agreementId}/activity${queryString(params)}`,
    );
    return { items: res.data, next_cursor: res.next_cursor };
  },
  getMyTasks: (params: { scope?: 'me' | 'all'; assignee?: string | null; from?: string; to?: string }) =>
    data(httpClient.get<Envelope<MyTasks>>(`${BASE}/my-tasks${queryString(params)}`)),
  /** M22a: an admin, or listings_work + widget:team_workload (others 403). No window = the Denver week (Monday–Sunday) holding today; at most 31 days. */
  getTeamWorkload: (params: { from?: string; to?: string }) =>
    data(httpClient.get<Envelope<TeamWorkload>>(`${BASE}/team-workload${queryString(params)}`)),
  /** M22c: the same window by who handed each task out, and those tasks. */
  getTeamWorkloadByAssigner: (params: { from?: string; to?: string }) =>
    data(httpClient.get<Envelope<TeamWorkloadByAssigner>>(`${BASE}/team-workload${queryString({ ...params, by: 'assigner' })}`)),
  /** M74: open broker requests nobody has yet, newest first (feature:listings_work; others 403). */
  getBrokerRequests: () => data(httpClient.get<Envelope<BrokerRequest[]>>(`${BASE}/broker-requests`)),
  listTemplates: (includeArchived = false) => data(httpClient.get<Envelope<TaskTemplate[]>>(`${BASE}/templates${queryString({ include_archived: includeArchived || undefined })}`)),
  searchUnits: (q: string, limit?: number) => data(httpClient.get<Envelope<SearchUnit[]>>(`${BASE}/unit-search${queryString({ q, limit })}`)),
  getFileUrl: (agreementId: string, fileId: string) => data(httpClient.get<Envelope<AgreementFileUrl>>(`${BASE}/agreements/${agreementId}/files/${fileId}/url`)),
  /** The Documents thumbnails: every live PDF / image signed in one call (1 hour). */
  getFilePreviewUrls: (agreementId: string) =>
    data(httpClient.get<Envelope<AgreementFilePreviewUrls>>(`${BASE}/agreements/${agreementId}/files/preview-urls`)),

  // ── Agreement writes ──
  createAgreement: (body: AgreementInput) => data(httpClient.post<Envelope<ListingAgreement>>(`${BASE}/agreements`, body)),
  updateAgreement: (agreementId: string, body: AgreementInput) => data(httpClient.patch<Envelope<ListingAgreement>>(`${BASE}/agreements/${agreementId}`, body)),
  /** M198: replace one property's links on the agreement (listings_work); answers them as the board lists them. */
  setPropertyLinks: (agreementId: string, propertyId: string, links: AgreementLink[]) =>
    data(httpClient.put<Envelope<PropertyLink[]>>(`${BASE}/agreements/${agreementId}/properties/${propertyId}/links`, { links })),
  addUnit: (agreementId: string, body: AddUnitInput) => data(httpClient.post<Envelope<unknown>>(`${BASE}/agreements/${agreementId}/listings`, body)),
  updateListing: (listingId: string, body: ListingInput) => data(httpClient.patch<Envelope<unknown>>(`${BASE}/listings/${listingId}`, body)),

  // ── Compliance (L3-1) ──
  closeAgreement: (agreementId: string, note?: string | null) =>
    data(httpClient.post<Envelope<ListingAgreement>>(`${BASE}/agreements/${agreementId}/close`, { note: note ?? null })),
  reopenAgreement: (agreementId: string, expirationDate?: string | null) =>
    data(httpClient.post<Envelope<ListingAgreement>>(`${BASE}/agreements/${agreementId}/reopen`, expirationDate ? { expiration_date: expirationDate } : {})),
  renewAgreement: (agreementId: string, body: { expiration_date: string; note?: string | null }) =>
    data(httpClient.post<Envelope<ListingAgreement>>(`${BASE}/agreements/${agreementId}/renew`, body)),
  /** M202: put on / take off hold (one feed row; no change answers the agreement as it is). */
  setAgreementHold: (agreementId: string, onHold: boolean) =>
    data(httpClient.post<Envelope<ListingAgreement>>(`${BASE}/agreements/${agreementId}/hold`, { on_hold: onHold })),
  /** M202: mark audited today (the API's Denver date). */
  auditAgreement: (agreementId: string) => data(httpClient.post<Envelope<ListingAgreement>>(`${BASE}/agreements/${agreementId}/audit`, {})),
  dismissDeal: (listingId: string, transactionId: string) =>
    data(httpClient.post<Envelope<{ already_dismissed: boolean }>>(`${BASE}/listings/${listingId}/deal-dismissals`, { transaction_id: transactionId })),

  /** M196 "Convert to deal": Active → Under contract; `agreement_warning` when the agreement has expired (a closed one is refused). */
  convertToDeal: (listingId: string) =>
    data(httpClient.post<Envelope<{ status: string; agreement_warning: AgreementWarning | null }>>(`${BASE}/listings/${listingId}/convert-to-deal`, {})),

  /** M222 "Give it its own workflow": an associated listing becomes a main one and gets its track's templates (one-way). */
  promoteListing: (listingId: string) =>
    data(httpClient.post<Envelope<{ status: string; tasks_created: number; steps_created: number }>>(`${BASE}/listings/${listingId}/promote`, {})),

  /** M196 "Deactivate <track> workflow", with a reason (Other needs a note): the listing → Withdrawn, its open tasks closed, the track's removal tasks added. */
  withdrawTrack: (listingId: string, body: { reason: ListingDeactivationReason; note: string | null }) =>
    data(
      httpClient.post<
        Envelope<{
          status: string;
          deactivation_reason: ListingDeactivationReason;
          deactivation_note: string | null;
          closed_task_ids: string[];
          removal_task_ids: string[];
          live_platforms: string[];
        }>
      >(`${BASE}/listings/${listingId}/withdraw`, body),
    ),

  // ── The request to Research (M30) ──
  getResearchMatches: (agreementId: string) => data(httpClient.get<Envelope<ResearchMatch[]>>(`${BASE}/agreements/${agreementId}/research-matches`)),
  answerResearchMatch: (agreementId: string, body: { broker_submission_id: string; property_id: string }) =>
    data(httpClient.post<Envelope<{ property: { id: string; name: string }; submission_acknowledged: boolean }>>(`${BASE}/agreements/${agreementId}/research-matches/use`, body)),
  dismissResearchMatch: (agreementId: string, body: { broker_submission_id: string; property_id: string }) =>
    data(httpClient.post<Envelope<{ property: { id: string; name: string } }>>(`${BASE}/agreements/${agreementId}/research-matches/dismiss`, body)),

  // ── Task writes ──
  addTask: (listingId: string, body: AddTaskInput) => data(httpClient.post<Envelope<ListingTask>>(`${BASE}/listings/${listingId}/tasks`, body)),
  updateTask: (taskId: string, body: TaskInput) => data(httpClient.patch<Envelope<ListingTask>>(`${BASE}/tasks/${taskId}`, body)),
  setStepDone: (stepId: string, done: boolean) => data(httpClient.patch<Envelope<TaskStep>>(`${BASE}/task-steps/${stepId}`, { done })),
  completeTask: (taskId: string, body: CompleteTaskInput) => data(httpClient.post<Envelope<ListingTask>>(`${BASE}/tasks/${taskId}/complete`, body)),
  reopenTask: (taskId: string) => data(httpClient.post<Envelope<ListingTask>>(`${BASE}/tasks/${taskId}/reopen`, {})),
  /** M16 2/3: an App verified task → Human confirmed. */
  confirmTask: (taskId: string) => data(httpClient.post<Envelope<ListingTask>>(`${BASE}/tasks/${taskId}/confirm`, {})),
  /** M16 2/3: an App verified task back to Open, saying why. */
  notYetTask: (taskId: string, note: string) => data(httpClient.post<Envelope<ListingTask>>(`${BASE}/tasks/${taskId}/not-yet`, { note })),
  /** M186 + D1: any open task except a takedown (409 TAKEDOWN_TASK / ALREADY_COMPLETED otherwise). */
  removeTask: (taskId: string) => data(httpClient.delete<Envelope<{ id: string; name: string }>>(`${BASE}/tasks/${taskId}`)),

  // ── Agreement documents ──
  /** The kind (and M198 `propertyId`, the property it is about) go before the file: the API reads the fields sent ahead of the file part. */
  uploadFile: async (agreementId: string, file: File, kind: AgreementFileKind, propertyId?: string | null): Promise<AgreementFile> => {
    const form = new FormData();
    form.append('kind', kind);
    if (propertyId) form.append('property_id', propertyId);
    form.append('file', file);
    const res = await fetch(`${env.API_URL || ''}${BASE}/agreements/${agreementId}/files`, {
      method: 'POST',
      body: form,
      credentials: 'include',
    });
    const body = (await res.json().catch(() => ({}))) as Partial<Envelope<AgreementFile>> & { error?: string; code?: string };
    if (!res.ok) throw new HttpClientError(res.status, body.error || 'Upload failed', body.code || 'UNKNOWN_ERROR', body);
    return body.data as AgreementFile;
  },
  updateFile,
  setFileKind: (agreementId: string, fileId: string, kind: AgreementFileKind) => updateFile(agreementId, fileId, { kind }),
  deleteFile: async (agreementId: string, fileId: string) =>
    (await httpClient.delete<Envelope<{ id: string; file_name: string; storage_object_removed: boolean }>>(`${BASE}/agreements/${agreementId}/files/${fileId}`)).data,
  /** D2-S F1: archive (off the board, the file kept) / restore one document. */
  archiveFile: (agreementId: string, fileId: string, reason?: string | null) =>
    data(httpClient.post<Envelope<ArchivedAgreementFile>>(`${BASE}/agreements/${agreementId}/files/${fileId}/archive`, reason ? { reason } : {})),
  restoreFile: (agreementId: string, fileId: string) =>
    data(httpClient.post<Envelope<AgreementFile>>(`${BASE}/agreements/${agreementId}/files/${fileId}/restore`, {})),

  // ── Templates (feature:listings_templates) ──
  createTemplate: (body: TemplateInput) => data(httpClient.post<Envelope<TaskTemplate>>(`${BASE}/templates`, body)),
  updateTemplate: (templateId: string, body: TemplateInput) => data(httpClient.patch<Envelope<TaskTemplate>>(`${BASE}/templates/${templateId}`, body)),
  setTemplateArchived: (templateId: string, archived: boolean) =>
    archived ? data(httpClient.post<Envelope<TaskTemplate>>(`${BASE}/templates/${templateId}/archive`, {})) : data(httpClient.post<Envelope<TaskTemplate>>(`${BASE}/templates/${templateId}/unarchive`, {})),
  reorderTemplates: async (track: TemplateTrack, templateIds: string[]) =>
    (await httpClient.put<Envelope<unknown>>(`${BASE}/templates/order`, { track, template_ids: templateIds })).data,
};
